import React, { Suspense, lazy } from 'react';
import { preloadLegal } from './LegalDocument';

// Публичные страницы-документы. Роутера в проекте нет и заводить его ради двух
// статических страниц не нужно: nginx на любой путь отдаёт index.html, а здесь
// путь разбирается один раз при загрузке. Переходы между этими страницами —
// обычные ссылки с полной перезагрузкой, история и состояние им не нужны.
//
// 🔴 ИЗМЕРЕНО 04.09.2026, и результат противоположен ожиданию: динамический
// `import()` внутри ложной ветки НЕ убирает чанк из сборки. Разделение кода webpack
// делает по графу модулей, до минификации; терсер потом сворачивает ветку внутри
// чанка, но сам файл уже создан и лежит в build/static/js. Замер: сборка onprem
// вынесла реквизиты из main.js, но оставила их в четырёх .chunk.js.
// Что это даёт и чего не даёт: клиент, открывший приложение, договор не скачивает —
// чанк не запрашивается. Но файл лежит у него на диске, и `grep` по build/ находит
// ИНН. Настоящее решение — не отдавать эти данные модулем JS вовсе;
// см. docs/backlog/client-edition-split.md.
const IS_SAAS = (process.env.REACT_APP_EDITION || 'saas') !== 'onprem';

// Загрузчики названы отдельно: ими же страница дочитывается до рендера (preloadPublicPage).
const LOADERS = IS_SAAS ? {
  '/oferta': () => import('./OfferPage'),
  '/consent': () => import('./ConsentPage'),
  '/privacy': () => import('./PrivacyPage'),
  '/cookie': () => import('./CookiePage'),
  '/trial': () => import('./TrialRequestPage'),
} : {};

// Текст документа лежит отдельным файлом (LegalDocument.js); у формы заявки его нет.
const LEGAL_SRC = {
  '/oferta': '/legal/offer.json',
  '/consent': '/legal/consent.json',
  '/privacy': '/legal/privacy.json',
  '/cookie': '/legal/cookie.json',
};

const OfferPage = IS_SAAS ? lazy(LOADERS['/oferta']) : null;
const ConsentPage = IS_SAAS ? lazy(LOADERS['/consent']) : null;
const PrivacyPage = IS_SAAS ? lazy(LOADERS['/privacy']) : null;
const CookiePage = IS_SAAS ? lazy(LOADERS['/cookie']) : null;
const TrialRequestPage = IS_SAAS ? lazy(LOADERS['/trial']) : null;

// Документ грузится чанком, поэтому нужен fallback. Пустой блок, а не спиннер:
// чанк локальный, ожидание в десятки миллисекунд, мелькающий спиннер хуже пустоты.
const withSuspense = (node) => <Suspense fallback={<div className="py-16" />}>{node}</Suspense>;

// Издание onprem не несёт ни договора, ни коммерции: платёжного контура там нет,
// продажи конечному пользователю тоже, значит и оферты, которую он принимает.
// Разбор: docs/superpowers/specs/2026-09-03-client-edition-boundary.md
const PAGES = IS_SAAS ? {
  '/oferta': () => withSuspense(<OfferPage />),
  '/consent': () => withSuspense(<ConsentPage />),
  '/privacy': () => withSuspense(<PrivacyPage />),
  '/cookie': () => withSuspense(<CookiePage />),
  // Лид-магнит: форма для организаций. Публичная по той же причине, что и
  // документы, — у пришедшего по ссылке нет ни учётки, ни токена.
  '/trial': () => withSuspense(<TrialRequestPage />),
} : {};

// Путь → страница или null. Хвостовой слэш и регистр не должны решать,
// увидит ли модератор ЮKassa оферту.
const normalize = (pathname) => (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';

export function publicPage(pathname) {
  const render = PAGES[normalize(pathname)];
  return render ? render() : null;
}

// Код страницы и текст документа — до рендера. Страница уже на экране готовым
// HTML (scripts/prerender.js, 13.10.2026): рисовать поверх неё пустоту на время
// загрузки — значит мигнуть и сбросить прокрутку. Отказ не ошибка: страница
// нарисуется обычным путём и сама скажет, чего не дождалась.
export function preloadPublicPage(pathname) {
  const key = normalize(pathname);
  const load = LOADERS[key];
  if (!load) return Promise.resolve();
  const text = LEGAL_SRC[key] ? preloadLegal(LEGAL_SRC[key]) : Promise.resolve();
  return Promise.all([load(), text]).then(() => undefined, () => undefined);
}

export const PUBLIC_PATHS = Object.keys(PAGES);
